export type ListValue = string | number | boolean | string[];
const prefix = "coprint:list-filters:v1:";
const maxRoutes = 150;

export function encodeListValue(value: ListValue): string {
  return Array.isArray(value) ? JSON.stringify(value) : String(value);
}

export function decodeListValue<T extends ListValue>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  if (typeof fallback === "string") return raw as T;
  if (typeof fallback === "boolean") return (raw === "true" ? true : raw === "false" ? false : fallback) as T;
  if (typeof fallback === "number") {
    const value = Number(raw);
    return (raw.trim() && Number.isSafeInteger(value) && value >= (fallback === 0 ? 0 : 1) ? value : fallback) as T;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (Array.isArray(value) && value.every(item => typeof item === "string")) return value as T;
  } catch { /* Malformed URLs use the normal list defaults. */ }
  return fallback;
}

export function rememberedSearch(search: string, keys: readonly string[]): string {
  const result = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.startsWith("f.") || keys.includes(key)) result.append(key, value);
  }
  return result.size ? `?${result}` : "";
}


export function shouldRestoreListSearch(previous: { pathname: string; key: string } | null, current: { pathname: string; key: string; search: string }, navigationType: string, saved: string): boolean {
  // Clearing a tab/filter on this page is intentional, even when it pushes history.
  // Only restore a plain return from another page (or the initial app load).
  return !current.search && Boolean(saved) && (previous === null
    || (previous.key !== current.key && navigationType !== "POP"
      && previous.pathname !== current.pathname));
}

export function createListMemory(scope: string, storage?: Storage) {
  const key = `${prefix}${scope}`;
  let routes: Record<string, string> = {};
  try {
    storage ??= window.sessionStorage;
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      routes = Object.fromEntries(Object.entries(parsed).filter(([path, search]) => path.startsWith("/") && typeof search === "string" && search.startsWith("?")).slice(-maxRoutes));
    }
  } catch { /* Navigation still works when storage is unavailable. */ }
  return {
    get: (path: string) => routes[path] ?? "",
    set(path: string, search: string) {
      if ((routes[path] ?? "") === search) return;
      delete routes[path];
      if (search) routes[path] = search;
      routes = Object.fromEntries(Object.entries(routes).slice(-maxRoutes));
      try { storage?.setItem(key, JSON.stringify(routes)); } catch { /* In-memory fallback. */ }
    }
  };
}

export function clearListMemory() {
  try {
    const storage = window.sessionStorage;
    for (const key of Object.keys(storage)) if (key.startsWith(prefix)) storage.removeItem(key);
  } catch { /* Storage may be disabled. */ }
}
